import { get } from 'lodash';
import OrderLineOptionsGetter from './orderlineOptionsGetter';
import { getCustomDiscountTags, isFreeMealPriceRule, isProductSpecificDiscount, isSunsetPointsPriceRule, } from '../priceRules/priceRulesHelpers';
import { extractValueFromOrderTags } from '../orders/OrderTags';
import { DomainCoreGeneration } from '../libraries/DomainCoreGeneration';
import { reduceRightWithParent } from '../orders/OrderHelpers';
import TenantMgr from '../tenants/TenantMgr';
import { getOrderLineDiscountValue, getOrderLinePrice } from './orderlineHelper';
const MY_GENERATION = DomainCoreGeneration.Generation5;
export const generateDiscountLine = (orderLines, orderTags, productCatalog, computedGlobalPriceRules, options) => {
    const extractTenantFromProductCatalog = (productCatalog) => {
        return productCatalog.products[0].tenantId;
    };
    const tenantId = extractTenantFromProductCatalog(productCatalog);
    const tenantData = TenantMgr(tenantId).getTenantData();
    const dailySpending = OrderLineOptionsGetter.dailySpending(options);
    const { totalPrice } = reduceRightWithParentFn(orderLines, computedGlobalPriceRules, tenantData, dailySpending, options);
    const [freeMealPriceRule, ...globalPriceRules] = [computedGlobalPriceRules.find(isFreeMealPriceRule)].concat(computedGlobalPriceRules.filter((rule) => !isFreeMealPriceRule(rule)));
    const bestDiscountP = globalPriceRules.reduce((acc, curr) => {
        var _a;
        const tags = [];
        const discountTags = getCustomDiscountTags(curr === null || curr === void 0 ? void 0 : curr.options);
        if (discountTags.length > 0) {
            tags.push(...discountTags);
        }
        const hasFreeMeal = extractValueFromOrderTags('FirstMeal', orderTags) === 'true';
        const freeMealBonus = get(freeMealPriceRule, 'options.discountMax', 100);
        let discountablePrice = ((_a = curr === null || curr === void 0 ? void 0 : curr.options) === null || _a === void 0 ? void 0 : _a.discountMax)
            ? Math.min(totalPrice, curr.options.discountMax + (hasFreeMeal ? freeMealBonus : 0) - dailySpending)
            : totalPrice;
        const freeMealDiscount = hasFreeMeal ? Math.min(discountablePrice, freeMealBonus) : 0;
        const isSunsetPoints = isSunsetPointsPriceRule(curr);
        if (discountablePrice !== totalPrice) {
            tags.push('DiscountCapped');
        }
        if (isSunsetPoints) {
            const sunsetPoints = options.sunsetPoints || {};
            discountablePrice = Math.round(sunsetPoints.totalPoints * curr.options.pointsCoefficient * 100) / 100;
        }
        const calcualteTotalDiscount = (discountablePrice, hasFreeMeal, freeMealBonus, discountPct) => {
            if (hasFreeMeal) {
                return (discountablePrice - freeMealBonus) * discountPct + freeMealBonus;
            }
            return discountablePrice * discountPct;
        };
        const totalDiscount = calcualteTotalDiscount(discountablePrice, hasFreeMeal, freeMealBonus, curr.options.discountPct);
        const hasReachedDailyLimit = dailySpending >= curr.options.discountMax;
        if (acc.totalDiscount < totalDiscount || hasReachedDailyLimit) {
            const totalAppliedDiscount = Math.min(totalDiscount, totalPrice);
            return {
                rule: curr,
                totalDiscount: hasReachedDailyLimit ? 0 : totalAppliedDiscount,
                freeMealDiscount,
                tags,
                hasReachedDailyLimit,
            };
        }
        return acc;
    }, {
        rule: null,
        totalDiscount: 0,
        freeMealDiscount: 0,
        tags: [],
        hasReachedDailyLimit: false,
    });
    const discountMax = get(bestDiscountP, 'rule.options.discountMax', null);
    const onlinePosId = get(bestDiscountP, 'rule.options.onlinePosId', null);
    const freeMealDiscount = {
        name: 'Gratis måltid',
        subLines: [],
        type: 'FreeMeal',
        generation: MY_GENERATION,
        totalPrice: -bestDiscountP.freeMealDiscount,
        tags: ['FreeMeal'],
    };
    const discountLine = {
        name: 'Total Rabat',
        subLines: [],
        type: 'Discount',
        tags: bestDiscountP.tags,
        totalPrice: -1 * (bestDiscountP.totalDiscount - bestDiscountP.freeMealDiscount),
        options: Object.assign(Object.assign({}, discountMax), { onlinePosId, appliedPriceRule: bestDiscountP && bestDiscountP.rule
                ? {
                    id: bestDiscountP.rule.id,
                    options: bestDiscountP.rule.options,
                }
                : undefined, hasReachedDailyLimit: bestDiscountP.hasReachedDailyLimit }),
        generation: MY_GENERATION,
    };
    return [freeMealDiscount, discountLine];
};
const reduceRightWithParentFn = (orderLines, computedGlobalPriceRules, tenantData, dailySpending, options) => {
    return reduceRightWithParent(orderLines, (acc, curr, parent) => {
        if (curr.type === 'Product') {
            if (parent !== null) {
                if (parent.type === 'CombinatorLine') {
                    return acc;
                }
                if (curr.skipInTotalRabat) {
                    return acc;
                }
                const currentQuantity = evaluateQuantity(curr);
                if (parent.options.freeQuantity) {
                    const parentFreeQuantity = acc.freeQuantityContainer[parent.virtualId] !== undefined
                        ? acc.freeQuantityContainer[parent.virtualId]
                        : parent.options.freeQuantity;
                    const currentPaidQuantity = currentQuantity - parentFreeQuantity;
                    const multiplyByQuantity = currentPaidQuantity < 0 ? 0 : currentPaidQuantity;
                    return Object.assign(Object.assign({}, acc), { freeQuantityContainer: Object.assign(Object.assign({}, acc.freeQuantityContainer), { [parent.virtualId]: parentFreeQuantity - currentQuantity < 0 ? 0 : parentFreeQuantity - currentQuantity }), totalPrice: acc.totalPrice + curr.price * multiplyByQuantity });
                }
                const excludedDiscountProductTypes = shouldOverrideGlobal(curr, computedGlobalPriceRules, tenantData);
                const totalPrice = excludedDiscountProductTypes.includes(curr.name)
                    ? acc.totalPrice
                    : acc.totalPrice + curr.totalPrice * (curr.name === 'Pant' ? parent.quantity : 1);
                return Object.assign(Object.assign({}, acc), { totalPrice });
            }
            let price = acc.totalPrice;
            let skipRabatCalculation = false;
            if (options.enableProductBestPriceInCatalogAndCart) {
                const olWithDiscount = isProductSpecificDiscount(curr.discount);
                if (olWithDiscount && computedGlobalPriceRules && computedGlobalPriceRules.length) {
                    const { discountMax, discountPct } = computedGlobalPriceRules[0].options;
                    const cartSpendings = dailySpending + acc.totalPrice;
                    let currentlyRemainingPLUSdiscount = Math.max((discountMax - cartSpendings) * discountPct, 0);
                    const originalPrice = getOrderLinePrice(curr, true);
                    const plusDiscount = originalPrice * discountPct;
                    currentlyRemainingPLUSdiscount = Math.min(currentlyRemainingPLUSdiscount, plusDiscount);
                    const discountValue = getOrderLineDiscountValue(curr);
                    skipRabatCalculation = discountValue > currentlyRemainingPLUSdiscount;
                }
            }
            if (!skipRabatCalculation) {
                price =
                    typeof curr.price === 'number' ? acc.totalPrice + curr.totalPrice : acc.totalPrice * evaluateQuantity(curr);
            }
            return Object.assign(Object.assign({}, acc), { totalPrice: price });
        }
        else if (curr.type === 'CombinatorLine') {
            return Object.assign(Object.assign({}, acc), { totalPrice: acc.totalPrice + curr.totalPriceAccum * evaluateQuantity(curr) });
        }
        return acc;
    }, { totalPrice: 0, freeQuantityContainer: {} }, null);
};
const evaluateQuantity = (curr) => {
    return curr.quantity || 1;
};
const shouldOverrideGlobal = (curr, computedGlobalPriceRules, tenantData) => {
    const freeIncludedEnum = {
        Pant: 'pant_included',
    };
    const generatedOptionKey = freeIncludedEnum[curr.name] || '';
    const shouldDiscountPant = computedGlobalPriceRules.some((priceRule) => priceRule.options[generatedOptionKey]);
    return get(tenantData, 'excludedDiscountProductTypes', []).filter((tags) => tags !== curr.name || !shouldDiscountPant);
};
